import React, { useState } from 'react';
import {
  DateParam,
  JsonParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import moment from 'moment';
import {
  Grid,
  MenuItem,
  TextField,
  Box,
  Stack,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DatePicker } from '@mui/x-date-pickers';

import { Point, CheckStatuses } from '@/types';
import { SearchPoints } from '@/pages/Points/components';
import { useAuth, useDebounce } from '@/hooks';
import { OrderDirections, ORDER_DIRECTIONS } from '@/utils/order-directions';
import { AppBar, DebouncedTextField, Empty, Error, Loader } from '@/components';
import { CheckFlat, TimesFlat } from '@/assets/icons';

import { ChecksTable } from './components';
import {useLazyGetChecksQuery} from "@/services/check";

export const CHECK_STATUSES = [
  {
    label: 'Проведён',
    value: CheckStatuses.CARRIED_OUT,
    icon: <CheckFlat sx={{ fontSize: 16 }} color="success" />,
  },
  {
    label: 'Отменён',
    value: CheckStatuses.CANCELLED,
    icon: <TimesFlat sx={{ fontSize: 16 }} color="error" />,
  },
];

const ChecksPage: React.FC = () => {
  const [{ search, order_direction, page, points, date_from, date_to, status }, setQuery] =
      useQueryParams({
        search: withDefault(StringParam, ''),
        page: withDefault(NumberParam, 1),
        points: withDefault<Partial<Point>[], Point[]>(JsonParam, []),
        order_direction: withDefault(StringParam, OrderDirections.DESC),
        status: withDefault(StringParam, ''),
        date_from: withDefault(DateParam, moment().toDate()),
        date_to: withDefault(DateParam, moment().toDate()),
      });

  const { user, isCashier, isRevisor, isAdmin } = useAuth();
  // дебаунс для точек
  const point_ids = useDebounce(points.map(p => p.id));
  // если ревизор или админ — обязательный фильтр точек
  const emptyFilters = (isRevisor || isAdmin) && point_ids.length === 0;

  // ленивый запрос
  const [trigger, { data: checks, isLoading, isFetching, isError, isSuccess }] =
      useLazyGetChecksQuery();

  const [hasSearched, setHasSearched] = useState(false);

  const buildRequest = (overridePage = page) => ({
    params: {
      search: search || null,
      order_direction,
      page: overridePage,
      order_by: 'id',
      point_ids: isCashier ? [user!.point!.id] : point_ids,
      status: status || null,
      date_from: date_from ? moment(date_from).format('YYYY-MM-DD') : null,
      date_to: date_to ? moment(date_to).format('YYYY-MM-DD') : null,
    },
  });

  const onSearch = () => {
    if (emptyFilters) return;
    setQuery({ page: 1 });
    setHasSearched(true);
    trigger(buildRequest(1));
  };

  const onChangePage = (newPage: number) => {
    setQuery({ page: newPage });
    trigger(buildRequest(newPage));
  };

  return (
      <>
        <AppBar title="Чеки" />

        <Grid container spacing={3}>
          <Grid item xs={12} className="no-print">
            <Stack direction="row" spacing={3} alignItems="flex-start">
              <Grid container spacing={2}>
                { (isRevisor || isAdmin) && (
                    <Grid item lg={3} md={3} sm={6} xs={12}>
                      <SearchPoints
                          multiple
                          value={points as Point[]}
                          disableCloseOnSelect
                          onChange={(_, opts) =>
                              setQuery({ points: opts.map(({ id, name }) => ({ id, name })) })
                          }
                          disabled={isLoading}
                          getOptionLabel={o => o.name}
                          renderInput={props => (
                              <TextField
                                  {...props}
                                  name="points"
                                  label="Точки"
                                  placeholder="Выберите из списка"
                                  variant="filled"
                                  size="small"
                              />
                          )}
                      />
                    </Grid>
                )}

                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DebouncedTextField
                      type="search"
                      name="search"
                      label="Поиск"
                      placeholder="Введите значение"
                      value={search}
                      onChange={e => setQuery({ search: e.target.value, page: 1 })}
                      variant="filled"
                      fullWidth
                      size="small"
                  />
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <TextField
                      select
                      value={order_direction}
                      label="Сортировать по"
                      disabled={isLoading}
                      onChange={e => setQuery({ order_direction: e.target.value })}
                      variant="filled"
                      fullWidth
                      size="small"
                  >
                    {ORDER_DIRECTIONS.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <TextField
                      select
                      value={status}
                      label="Статус"
                      disabled={isLoading}
                      onChange={e => setQuery({ status: e.target.value })}
                      variant="filled"
                      fullWidth
                      size="small"
                  >
                    <MenuItem value="">Все</MenuItem>
                    {CHECK_STATUSES.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DatePicker
                      mask="__.__.____"
                      label="Дата от"
                      value={date_from}
                      minDate={moment(date_to).subtract(6, 'months')}
                      maxDate={moment(date_to)}
                      onChange={v => setQuery({ date_from: v ? moment(v).toDate() : undefined })}
                      disabled={isLoading || isFetching}
                      renderInput={params => (
                          <TextField {...params} variant="filled" size="small" fullWidth />
                      )}
                  />
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DatePicker
                      mask="__.__.____"
                      label="Дата до"
                      value={date_to}
                      maxDate={moment()}
                      onChange={v => setQuery({ date_to: v ? moment(v).toDate() : undefined })}
                      disabled={isLoading || isFetching}
                      renderInput={params => (
                          <TextField {...params} variant="filled" size="small" fullWidth />
                      )}
                  />
                </Grid>

                {/* Кнопка «Поиск» */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <LoadingButton
                      loading={isFetching}
                      disabled={isFetching || emptyFilters}
                      onClick={onSearch}
                      size="large"
                      variant="contained"
                      fullWidth
                  >
                    Поиск
                  </LoadingButton>
                </Grid>
              </Grid>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            {emptyFilters ? (
                <Empty text="Выберите фильтр по точкам" />
            ) : !hasSearched ? (
                <Empty text="Нажмите «Поиск», чтобы увидеть данные" />
            ) : isLoading ? (
                <Loader />
            ) : isError ? (
                <Error text="Произошла ошибка с загрузкой чеков! Пожалуйста, попробуйте позже" />
            ) : isSuccess && checks?.data?.length ? (
                <ChecksTable
                    list={checks}
                    page={page}
                    disabled={isFetching}
                    onChangePage={onChangePage}
                />
            ) : (
                <Empty text="Чеки отсутствуют" />
            )}
          </Grid>
        </Grid>
      </>
  );
};

export default ChecksPage;
